import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setLanguage } from '../../../../features/langSlice';
import { Icon } from '../../../elements/icon';
import CustomLink from '../../../CustomLink';
import useMeta from '../../../../hooks/useMeta';
import { logo_home } from '../../../../Data/Images/Home';
import '../../../../assets/css/landing-pages.css';
import Preview1 from '../../../../assets/1-preview.webp';
import Preview3 from '../../../../assets/3-preview.webp';
import Preview8 from '../../../../assets/8-preview.webp';
import FabienneImg from '../../../../assets/testimonials/Fabienne.webp';

// ─────────────────────────────────────────────────────────────────────────
// /prequiz/guesswork — first entry in the /prequiz/(angle) page type.
// A single-viewport, meta-ads pre-quiz interstitial (Obvi-style): logo,
// social proof, headline, trust row, a rotating real-review card, sticky
// CTA into /quiz. Deliberately much shorter than the full /pages/stop-
// guessing landing page — this is a quiz-intro screen, not a sales page.
// Review quotes are curated from real weriise.co reviews.io submissions.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const REVIEWS = [
  { quoteKey: 'PQ_Guess_Review1Quote', nameKey: 'PQ_Guess_Review1Name' },
  { quoteKey: 'PQ_Guess_Review2Quote', nameKey: 'PQ_Guess_Review2Name' },
  { quoteKey: 'PQ_Guess_Review3Quote', nameKey: 'PQ_Guess_Review3Name' },
  { quoteKey: 'PQ_Guess_Review4Quote', nameKey: 'PQ_Guess_Review4Name' },
  { quoteKey: 'PQ_Guess_Review5Quote', nameKey: 'PQ_Guess_Review5Name' },
];

const ROTATE_MS = 4500;

const StarRow = ({ count = 5, size = 18 }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

const LangSwitcher = () => {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const selected = (i18n.language || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchTo = (lng) => {
    localStorage.setItem('lang', lng);
    i18n.changeLanguage(lng);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lang', lng);
    navigate({ search: newParams.toString() });
    dispatch(setLanguage({ lng }));
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'absolute', top: 16, right: 16 }}>
      <button
        onClick={() => setOpen(!open)}
        className="lp-lang-btn"
        style={{ ...SANS, background: 'none', border: '1px solid rgba(16,54,66,0.2)',
          borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#103642',
          display: 'flex', alignItems: 'center', gap: 5 }}
      >
        {selected.toUpperCase()}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="#103642" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          overflow: 'hidden', minWidth: 110, zIndex: 200,
        }}>
          {[{ code: 'de', label: 'Deutsch' }, { code: 'en', label: 'English' }].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => switchTo(code)}
              style={{ ...SANS, display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: selected === code ? '#f5f7f7' : '#fff',
                cursor: 'pointer', fontSize: 13, color: '#103642',
                fontWeight: selected === code ? 600 : 400, textAlign: 'left' }}
            >
              <i className={`flag-icon flag-icon-${code === 'en' ? 'us' : 'de'}`} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReviewCard = ({ t }) => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % REVIEWS.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const goTo = (i) => {
    clearInterval(timerRef.current);
    setIndex(i);
    timerRef.current = setInterval(() => {
      setIndex((cur) => (cur + 1) % REVIEWS.length);
    }, ROTATE_MS);
  };

  const review = REVIEWS[index];

  return (
    <div>
      <div style={{
        background: '#f5f7f7', borderRadius: 16, padding: '24px 20px',
        display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center',
        textAlign: 'center', minHeight: 168,
      }}>
        <StarRow />
        <p key={index} style={{ ...SANS, fontSize: 15, fontStyle: 'italic',
          color: '#103642', margin: 0, lineHeight: 1.6 }}>
          "{t(review.quoteKey)}"
        </p>
        <p style={{ ...SANS, fontSize: 13, fontWeight: 600, color: 'rgba(16,54,66,0.7)', margin: 0 }}>
          {t(review.nameKey)} <span style={{ fontWeight: 400, color: 'rgba(16,54,66,0.4)' }}>· {t('PQ_Guess_VerifiedBuyer')}</span>
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
        {REVIEWS.map((_, i) => (
          <button
            key={i}
            aria-label={`review ${i + 1}`}
            onClick={() => goTo(i)}
            style={{
              width: i === index ? 18 : 6, height: 6, borderRadius: 999, border: 'none',
              padding: 0, cursor: 'pointer', transition: 'width 0.25s ease, background 0.25s ease',
              background: i === index ? '#116D6E' : 'rgba(16,54,66,0.15)',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const TrustItem = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <Icon name="tealCheckCircle" size={15} />
    <p style={{ ...SANS, fontSize: 13, fontWeight: 600, color: '#103642', margin: 0 }}>{children}</p>
  </div>
);

const PrequizGuesswork = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { metaEvent } = useMeta();

  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    metaEvent('ViewContent');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const avatars = [Preview8, FabienneImg, Preview1, Preview3];

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, position: 'relative' }}>
      <Helmet>
        <title>Schluss mit dem Raten. | riise.</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh',
        display: 'flex', flexDirection: 'column', padding: '0 0 140px 0' }}>

        {/* Header */}
        <div style={{ position: 'relative', padding: '24px 24px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img src={logo_home} alt="riise" style={{ height: 22, width: 'auto' }} />
          </div>
          <LangSwitcher />
        </div>

        {/* Decorative progress hint */}
        <div style={{ padding: '0 24px 20px' }}>
          <div style={{ height: 4, borderRadius: 999, background: '#eee', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '8%', borderRadius: 999, background: '#116D6E' }} />
          </div>
        </div>

        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 24, flex: 1 }}>

          {/* Social proof row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex' }}>
              {avatars.map((src, i) => (
                <img key={i} src={src} alt="" style={{
                  width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
                  border: '2px solid #fff', marginLeft: i === 0 ? 0 : -10,
                }} />
              ))}
            </div>
            <p style={{ ...SANS, fontSize: 13, fontWeight: 600, color: '#103642', margin: 0, lineHeight: 1.4 }}>
              {t('PQ_Guess_HeroKicker')}
            </p>
          </div>

          {/* Headline */}
          <h1 style={{ ...SERIF, fontSize: 30, lineHeight: 1.16, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t('PQ_Guess_H1Part1')}{' '}
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t('PQ_Guess_H1Part2')}</em>
          </h1>

          {/* Subline */}
          <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)',
            lineHeight: 1.65, margin: 0, textAlign: 'center' }}>
            {t('PQ_Guess_Subline')}
          </p>

          {/* Trust row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 20px' }}>
            <TrustItem>{t('PQ_Guess_Trust1')}</TrustItem>
            <TrustItem>{t('PQ_Guess_Trust2')}</TrustItem>
            <TrustItem>{t('PQ_Guess_Trust3')}</TrustItem>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0' }} />

          {/* Rotating reviews */}
          <div>
            <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'rgba(16,54,66,0.4)', margin: '0 0 14px 0',
              textAlign: 'center' }}>
              {t('PQ_Guess_ReviewsLabel')}
            </p>
            <ReviewCard t={t} />
          </div>
        </div>
      </div>

      {/* Sticky CTA */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: '14px 24px calc(14px + env(safe-area-inset-bottom))',
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <CustomLink
            to="/quiz"
            onClick={() => metaEvent('Lead')}
            className="lp-btn-cta"
            style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, background: '#F6E147', color: '#103642',
              fontWeight: 700, fontSize: 16, borderRadius: 999,
              padding: '16px 36px', textDecoration: 'none', width: '100%' }}
          >
            {t('PQ_Guess_Cta')}
            <span aria-hidden="true">→</span>
          </CustomLink>
          <p style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)',
            textAlign: 'center', margin: '8px 0 0 0' }}>
            {t('PQ_Guess_CtaSub')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrequizGuesswork;
